import type { BriefCall } from '../../engine/bench/brief';
import { BRIEF_EFFORT } from '../../engine/bench/brief';
import type { JudgeFn } from '../../engine/bench/guard';
/**
 * The two injected functions of the v2 loop (`docs/reports/bench-v2-loop.md`, "The interface WP6 and WP7 build on"), built
 * on a `Provider`: `completeFn` maps a `ChunkRequest` to a provider request and honours the loop's `maxTokens` (the single
 * retry at 64,000); `judgeFn` runs the dialogue guard's judge call. Both send the run's model and effort, and `output_config`
 * carries the request's schema. A `refusal` is returned as `stopReason: 'refusal'`, never thrown (the loop declines the unit).
 */
import type { CompleteFn, ProviderAnswer } from '../../engine/bench/loop2';
import type { CompletionRequest, CompletionResponse, Provider } from '../../engine/bench/types';
import { DEFAULT_MODEL, type Effort, JUDGE_EFFORT, LATEST_SONNET } from '../../shared/models';
import { DEFAULT_MAX_TOKENS } from './request';

export interface AdapterOptions {
	model?: string;
	/** `BENCH_EFFORT`: `high`, the pilot's choice (DESIGN ED-58, 2026-10-03; ED-26 left the effort to experiment). */
	effort?: Effort;
	maxTokens?: number;
}

export const DEFAULT_EFFORT: Effort = 'high';

/** The judge's effort (ED-96): `low`; re-exported from `shared/models` so the cost model shares it. */
export { JUDGE_EFFORT };

function toAnswer(res: CompletionResponse): ProviderAnswer {
	return {
		parsed: res.parsed ?? res.text,
		stopReason: res.stopReason,
		...(res.usage ? { usage: res.usage } : {}),
	};
}

/** The provider request of a chunk (exposed for the Economy driver, which batches the same requests). */
export function chunkCompletionRequest(
	request: { system: string; user: string; schema: object },
	opts: AdapterOptions & { maxTokens?: number } = {}
): CompletionRequest {
	return {
		system: request.system,
		user: request.user,
		schema: request.schema,
		model: opts.model ?? DEFAULT_MODEL,
		effort: opts.effort ?? DEFAULT_EFFORT,
		maxTokens: opts.maxTokens ?? DEFAULT_MAX_TOKENS,
		cacheSystem: true,
	};
}

export function completeFn(provider: Provider, options: AdapterOptions = {}): CompleteFn {
	return async (request, opts) =>
		toAnswer(
			await provider.complete(
				chunkCompletionRequest(request, {
					...options,
					maxTokens: opts.maxTokens ?? options.maxTokens,
				})
			)
		);
}

/**
 * The document brief's call (BENCH-V3-PLAN §8, ED-89): the latest Sonnet at `BRIEF_EFFORT` (`medium`), whatever model the
 * run rewrites with, `max_tokens` from the caller (8,000, then 16,000 once). The system text is the brief's own and is short;
 * it is sent uncached (one request per window, no repeats).
 */
export function briefFn(provider: Provider): BriefCall {
	return async (request, opts) => {
		const res = await provider.complete({
			system: request.system,
			user: request.user,
			schema: request.schema,
			model: LATEST_SONNET,
			effort: BRIEF_EFFORT,
			maxTokens: opts.maxTokens,
			cacheSystem: false,
		});
		return {
			parsed: res.parsed ?? res.text,
			stopReason: res.stopReason,
			...(res.usage ? { usage: res.usage } : {}),
		};
	};
}

/**
 * The judge's reply is small and has no retry at 64,000: a truncated answer reaches the guard as invalid. It runs at
 * `JUDGE_EFFORT` (`low`, ED-96) unless the caller passes an effort explicitly (the pilot tool's `judge <effort>` command).
 */
export function judgeFn(provider: Provider, options: AdapterOptions = {}): JudgeFn {
	return async (request) => {
		const res = await provider.complete(
			chunkCompletionRequest(request, {
				...options,
				effort: options.effort ?? JUDGE_EFFORT,
				maxTokens: options.maxTokens ?? 8_000,
			})
		);
		return res.parsed ?? res.text;
	};
}
